import { Form } from 'antd';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import DraggableTable from '../projects/ProjectScheduleOfValues/DraggableTable';
import BorderlessButton from '../common/buttons/BorderlessButton';
import Colors from '../constants/Colors';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import useToggle from '../common/hooks/useToggle';
import SimpleFormModal from '../common/modals/SimpleFormModal';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import FormColorPicker from '../common/inputs/FormColorPicker';

const onHeaderCell = () => ({
  style: {
    backgroundColor: 'white',
  },
});

function BoardColorsConfigure({
  value,
  onChange,
}) {
  const colors = useMemo(() => value ?? [], [value]);
  const [form] = Form.useForm();
  const { toggle, isToggled } = useToggle();
  const [selectedEntry, setSelectedEntry] = useState();

  useEffect(() => {
    if (!isToggled) {
      setSelectedEntry();
      return form.resetFields();
    }

    return form.setFieldsValue({
      ...(selectedEntry || {}),
      color: selectedEntry?.color || '#FFFFFFFF',
    });
  }, [isToggled, form, selectedEntry]);

  const onDeleteClicked = useCallback((record) => {
    CustomConfirmModal({
      title: `Delete Color ${record.label}?`,
      content: 'Are you sure you want to delete this color?',
      onOk: () => {
        onChange(colors.filter((entry) => entry.id !== record.id));
      },
      okText: 'Delete',
    });
  }, [colors, onChange]);

  const closeModal = useCallback(() => {
    setSelectedEntry();
    toggle();
  }, [toggle]);

  const onSave = useCallback(() => {
    const payload = form.getFieldsValue();
    if (selectedEntry) {
      onChange(colors.map((entry) => (
        entry.id === selectedEntry.id
          ? { ...entry, ...payload }
          : entry
      )));
    } else {
      onChange(colors.concat({
        id: `new-${DateTime.local().toMillis()}`,
        orderIndex: colors.length,
        isNew: true,
        ...payload,
      }));
    }
    closeModal();
  }, [form, closeModal, selectedEntry, colors, onChange]);

  const onMove = useCallback((id, _, newIndex) => {
    const sourceIndex = colors.findIndex((entry) => entry.id === id);
    if (sourceIndex < 0 || sourceIndex === newIndex) return;
    const newColors = [...colors];
    const [moved] = newColors.splice(sourceIndex, 1);
    newColors.splice(newIndex, 0, moved);
    onChange(newColors.map((entry, idx) => ({
      ...entry,
      orderIndex: idx,
    })));
  }, [colors, onChange]);

  const columns = useMemo(() => (
    [
      {
        title: 'Color',
        dataIndex: 'color',
        key: 'color',
        width: 50,
        onHeaderCell,
        render: (color) => (
          <FormColorPicker value={color} showText={false} style={{ marginRight: 35 }} />
        ),
      },
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
        onHeaderCell,
      },
      {
        dataIndex: 'edit',
        align: 'edit',
        width: 60,
        onHeaderCell,
        render: (_, record) => (
          <BorderlessButton
            key={record.id}
            title=""
            icon="edit"
            color={Colors.ONTRACCR_BLACK}
            onClick={() => {
              setSelectedEntry(record);
              toggle();
            }}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
            }}
          />
        ),
      },
      {
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        width: 60,
        onHeaderCell,
        render: (_, record) => (
          <BorderlessButton
            title=""
            icon="delete"
            color={Colors.opacity(Colors.ONTRACCR_RED, 1)}
            onClick={() => onDeleteClicked(record)}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
              cursor: 'pointer',
            }}
          />
        ),
      },
    ]
  ), [onDeleteClicked, toggle]);

  return (
    <>
      <OnTraccrButton
        title="Add"
        type="primary"
        onClick={toggle}
        style={{ marginBottom: 8 }}
      />
      <DraggableTable
        columns={columns}
        dataSource={colors}
        pagination={false}
        size="small"
        onMove={onMove}
        isDraggable
        rowKey="id"
      />
      <SimpleFormModal
        title={`${selectedEntry ? 'Edit' : 'Add'} Color`}
        visible={isToggled}
        onSave={onSave}
        onClose={closeModal}
      >
        <div style={{ width: '100%', display: 'inline-flex' }}>
          <Form form={form} layout="inline">
            <Form.Item name="color">
              <FormColorPicker isNotDisplay showText={false} style={{ marginLeft: 35 }} />
            </Form.Item>
            <Form.Item
              name="label"
              label="Label"
              style={{ width: 'inherit', marginLeft: 35 }}
              rules={[{ required: true, message: 'Please enter a label' }]}
            >
              <OnTraccrTextInput placeholder="Enter Label" />
            </Form.Item>
          </Form>
        </div>
      </SimpleFormModal>
    </>
  );
}

BoardColorsConfigure.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default BoardColorsConfigure;
